import { useCallback } from 'react';
import type { PatientCostVisitContext } from '@features/patient-coverages/types';
import {
  collectiblePatientAmount,
  type PatientCost,
} from '@features/patient-coverages/utils/patientCost';
import type { PaymentSheetRequest } from '@features/patient-ledger';
import {
  useOpenEligibilityDetail,
  useOpenLedgerDrawer,
  useOpenPaymentSheet,
} from '@features/patient-ledger';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import type { EligibilitySource } from '../types/source';

/**
 * Per-call input for opening a payment or eligibility surface from an
 * eligibility trigger (popover, summary, edit-appointment slide, worklist
 * row).
 *
 * `encounterId` and `appointmentId` are intentionally separate fields —
 * encounters and appointments are distinct resources, and conflating them
 * 404s the encounter-scoped collection endpoint. Pass each in the matching
 * slot.
 *
 * Defaults come from a validated fixed co-pay component or an exact-visit
 * confirmed total. The legacy `memberObligationCents` input is ignored.
 *
 * `source` is the analytics tag carried into Statsig events. Loosely typed
 * (`EligibilitySource | string`) so prototype surfaces can use their own
 * tags without lying through the type system.
 */
export interface EligibilityActionInput {
  patientId?: string;
  patientName?: string;
  encounterId?: string;
  appointmentId?: string;
  /** ISO start time for the pinned visit, used to label fallback encounter options. */
  contextVisitStartsAt?: string | null;
  /** Legacy input is ignored; only a validated projection can supply a default. */
  memberObligationCents?: number | null;
  patientCost?: PatientCost | null;
  visitContext?: Partial<PatientCostVisitContext>;
  source: EligibilitySource | (string & {});
}

export interface EligibilityActions {
  /**
   * Open the settle surface pre-targeting the encounter-linked copay
   * invoice flow.
   */
  openInvoice: (input: EligibilityActionInput) => void;
  /**
   * Open the settle surface pre-targeting the external-collection flow,
   * with method=external. In encounter scope the BE call uses
   * `create_charge=true` so the encounter mints a fresh charge_line +
   * applies the payment in one shot — the eligibility badge can then flip
   * `collect → collected`.
   */
  openLogCollection: (input: EligibilityActionInput) => void;
  /**
   * Open eligibility detail. Used for ambient row clicks / badge clicks
   * where the user wants to inspect coverage rather than take a payment
   * action.
   *
   * `autoOpen.verificationForm` pre-expands the verification log form on
   * mount — used by worklist rows in blocked / attention / expired states.
   */
  openDrawer: (input: EligibilityActionInput, autoOpen?: { verificationForm?: boolean }) => void;
}

/**
 * Single helper for the three eligibility-origin triggers. Centralizes the
 * (patientId, encounterId, appointmentId, amount, source) wiring so drift
 * here can't 404 the BE the way it did pre-unification.
 *
 * Gated (gdn-2957) they land on two surfaces, split by what they are:
 * the two money verbs open the payment sheet, and openDrawer opens
 * eligibility detail — which is its own modal again, having spent a while
 * as a tab inside the money drawer. Ungated, all three still open the
 * drawer with different initial state.
 */
export function useEligibilityActions(): EligibilityActions {
  const openLedgerDrawer = useOpenLedgerDrawer();
  const openPaymentSheet = useOpenPaymentSheet();
  const openEligibilityDetail = useOpenEligibilityDetail();
  // gdn-2957: gated, money verbs open the payment sheet and eligibility
  // detail opens its own modal. Ungated users keep the drawer for both.
  const showBalanceTable = useFeatureGate(FeatureGates.PATIENT_BALANCE_TABLE);

  const openMoneyAction = useCallback(
    (request: PaymentSheetRequest) => {
      if (showBalanceTable) {
        openPaymentSheet(request);
        return;
      }
      openLedgerDrawer({
        patientId: request.patientId,
        patientName: request.patientName,
        encounterId: request.encounterId ?? undefined,
        contextVisitStartsAt: request.contextVisitStartsAt,
        defaultAmountCents: request.defaultAmountCents,
        initialTab: 'ledger',
        initialAction: request.action,
        source: request.source,
      });
    },
    [showBalanceTable, openPaymentSheet, openLedgerDrawer],
  );

  const openInvoice = useCallback(
    (input: EligibilityActionInput) => {
      if (!input.patientId) return;
      openMoneyAction({
        patientId: input.patientId,
        patientName: input.patientName ?? null,
        encounterId: input.encounterId,
        contextVisitStartsAt: input.contextVisitStartsAt,
        defaultAmountCents: collectiblePatientAmount(input.patientCost, input.visitContext),
        action: {
          kind: 'settle',
          method: 'invoice',
          paymentIntent: 'copay',
        },
        source: input.source,
      });
    },
    [openMoneyAction],
  );

  const openLogCollection = useCallback(
    (input: EligibilityActionInput) => {
      if (!input.patientId) return;
      openMoneyAction({
        patientId: input.patientId,
        patientName: input.patientName ?? null,
        encounterId: input.encounterId,
        contextVisitStartsAt: input.contextVisitStartsAt,
        defaultAmountCents: collectiblePatientAmount(input.patientCost, input.visitContext),
        action: { kind: 'settle', method: 'external' },
        source: input.source,
      });
    },
    [openMoneyAction],
  );

  const openDrawer = useCallback(
    (input: EligibilityActionInput, autoOpen?: { verificationForm?: boolean }) => {
      if (!input.patientId) return;
      const initialForm = autoOpen?.verificationForm ? 'verification' : undefined;
      if (showBalanceTable) {
        openEligibilityDetail({
          patientId: input.patientId,
          patientName: input.patientName ?? null,
          encounterId: input.encounterId,
          contextVisitStartsAt: input.contextVisitStartsAt,
          defaultAmountCents: collectiblePatientAmount(input.patientCost, input.visitContext),
          initialForm,
          source: input.source,
        });
        return;
      }
      openLedgerDrawer({
        patientId: input.patientId,
        patientName: input.patientName ?? null,
        encounterId: input.encounterId,
        contextVisitStartsAt: input.contextVisitStartsAt,
        defaultAmountCents: collectiblePatientAmount(input.patientCost, input.visitContext),
        initialTab: 'eligibility',
        initialEligibilityForm: initialForm,
        source: input.source,
      });
    },
    [showBalanceTable, openEligibilityDetail, openLedgerDrawer],
  );

  return { openInvoice, openLogCollection, openDrawer };
}
